import React from 'react';
import { Link } from 'react-router-dom';
import './HistoryPage.css';

const ledger = [
  { big: '2024',     lbl: '— Founded',              sub: 'RussellStone Lighthouse formally established in September.' },
  { big: '8',        lbl: '— Founding consultants', sub: 'Akzeon TOC certified in the USA, October 2024.' },
  { big: '3',        lbl: '— Partners',             sub: 'RussellStone Group · Akzeon · CCI Professional.' },
  { big: 'Aug 2025', lbl: '— Public engagements',   sub: 'Formal external engagements began.' },
];

const chapters = [
  {
    band: 'band--paper-warm',
    roman: 'II',
    cat: 'Chapter I · pre-2023',
    title: <>The problem <em>we</em> lived.</>,
    lede: 'Before there was a method, there was the frustration of running businesses well enough to see how poorly we could explain why some succeeded and others did not.',
    year: 'Pre-2023',
    chap: '▸ The frustration',
    head: <>No reliable way to diagnose what was <em>actually</em> wrong.</>,
    prose: [
      'After years of books, mixed industries, and tried approaches, we still had no reliable way to identify what was actually wrong with a company or how to fix it.',
      'RussellStone Group had taken on many ventures. Some became remarkable successes in places we did not expect. Others failed despite looking like sure things at the start. We could not tell which was which before the fact.',
      'We were constantly solving problems, and new problems kept replacing the old ones. The flavour changed. The work did not stop. We could not enjoy the wins because we were always bleeding in the other column.',
      'On top of that, we struggled to hold the buy-in of our teams through hard changes. There had to be a better way.',
    ],
    movesHead: '— What we noticed',
    moves: [
      'Books, experience, governance, culture. All gave conflicting answers.',
      'We suspected we were working at the wrong level. Treating symptoms of deeper problems.',
      'Constant firefighting, shifting metrics, no steady read on what was working.',
      'We knew there had to be a structure that made this learnable rather than lucky.',
    ],
  },
  {
    band: 'band--prussian',
    roman: 'III',
    cat: 'Chapter II · 2023',
    title: <>Finding the <em>anchor</em>.</>,
    lede: 'The shared language we had been circling independently finally appeared in print and in person, in the same year.',
    year: '2023',
    chap: '▸ The discovery',
    head: <>“The Goal”, a team forming, then <em>Ray</em>.</>,
    prose: [
      'We reread “The Goal” by Eli Goldratt. The first link in what would become a cohesive picture. We worked through the rest of his books and tried to implement them with limited success.',
      'Through the year, our team formed around this shared excitement. Each of us had been circling the same idea independently. It only started to land when we began working on it together.',
      'Russell reached out to Ray Immelman at Akzeon in September. Russell had read Ray’s book “Great Boss Dead Boss” fifteen years earlier and used the motivational model as part of the foundation for RussellStone Group.',
      'Ray came to South Africa in November, for his brother’s wedding, and gave us our first proper training in Theory of Constraints. That visit set the direction.',
    ],
    movesHead: '— What moved',
    moves: [
      'Team worked through Goldratt’s books across the year.',
      'Shared language emerged. We stopped circling and started building.',
      'Russell reconnected with Ray Immelman after a 15-year gap.',
      'Ray’s November visit gave the team its first formal TOC training.',
      'First implementation attempts inside RussellStone Group companies.',
    ],
    teamNote: 'The team formed gradually through 2023. Individual dreams became a shared one, and Ray’s November training gave the shared dream a structure.',
  },
  {
    band: 'band--paper-warm',
    roman: 'IV',
    cat: 'Chapter III · 2024',
    title: <>Building <em>ability</em>.</>,
    lede: 'The year we became a firm. Formally, and in the way we worked. Theory met clients, and the approach changed shape because of it.',
    year: '2024',
    chap: '▸ The formation',
    head: <>From “teach the company” to <em>partner with it</em>.</>,
    prose: [
      'Through 2024 we kept developing the team and began informal client work to refine the approach. We formally established RussellStone Lighthouse in September as a consultancy within RussellStone Group.',
      'In October we sent eight of us to the USA for two weeks of intensive Akzeon certification. We learned the patterns behind what has historically worked, and the tools that unlocked those results.',
      'In parallel we took on our first clients, starting with connections inside the Group. Real engagements taught us things theory could not.',
      'The biggest lesson: training and theory transfer were not enough. The approach had to evolve. We moved from “teach the company to implement” to the partnership model we use now. We filter the theory to the level your people can act on, work alongside a core team, and help them adopt the method to a depth that changes outcomes.',
    ],
    movesHead: '— What moved',
    moves: [
      'RussellStone Lighthouse formally established, September 2024.',
      'Eight of us completed Akzeon TOC certification in the USA, October.',
      'First client work began through RussellStone Group connections.',
      'Approach shifted from training-led to partnership-led.',
      'Learned to filter theory into practical action with guided adoption.',
      'Learned to work through the core team rather than over their heads.',
    ],
    teamNote: 'Our eight founding consultants completed Akzeon TOC certification during this period. That is the shared foundation every engagement runs on.',
  },
  {
    band: 'band--prussian',
    roman: 'V',
    cat: 'Chapter IV · 2025',
    title: <>The partnership model, in public.</>,
    lede: 'By August 2025 the method had taken shape, and we began formal external engagements. We had become what we first set out to be.',
    year: '2025',
    chap: '▸ The foundation',
    head: <>We <em>partner</em>.</>,
    prose: [
      'After finding our balance through client work, we began formal external engagements in August 2025. By then we were what we first set out to be. A consultancy that works with companies carrying potential.',
      'The method had taken shape. We do not just train or implement. We partner, to help a business develop its own way of seeing and solving its constraints. We filter complex theory into practical action, work alongside the core team, and leave ability behind rather than reliance.',
      'Sitting inside RussellStone Group gives us the stability most new firms lack. That lets us price on success fees rather than hours, take a longer view, and align our outcome directly with yours.',
      'We learned to work inside uncertainty. We want to share that with other businesses facing the same thing.',
    ],
    movesHead: '— What moved',
    moves: [
      'Formal external engagements began, August 2025.',
      'Positioned for businesses with potential.',
      'Partnership model transfers ability to the team.',
      'Success-fee model aligns our outcome with yours.',
      'Group backing lets us take the long view.',
    ],
    teamNote: 'The team today brings together leadership and industry experience, systems implementation, cultural change, operational engineering, financial management, and specialists who leave ability behind. All unified by the Akzeon TOC certification and the partnership model it sits inside.',
  },
];

const partners = [
  {
    logo:  '/img/partnerlogos/russellStone-group-logo284x152.webp',
    alt:   'RussellStone Group',
    chap:  '▸ Partner · 01',
    role:  <em>Parent company</em>,
    body:  'Founded in 2003. A privately-owned investment house with operations across six countries, over 1,600 people, and 50+ companies spanning agriculture, financial services, foods, energy, and property. Twenty years of building and running businesses, hands-on asset management, and long-horizon stability that lets us price on success rather than hours.',
    href:  'https://russellstone.co.za/',
    label: 'Visit RS Group →',
  },
  {
    logo:  '/img/partnerlogos/Akzeon-Web-Logo-172x41.webp',
    alt:   'Akzeon Inc',
    chap:  '▸ Partner · 02',
    role:  <em>Certification &amp; mentorship</em>,
    body:  'Global consulting firm focused on constraint identification and resolution. Our eight founding consultants completed two weeks of intensive certification in the USA in October 2024. Mentorship from Ray Immelman and the Akzeon team has continued since. The methods that sit behind the partnership model come from this relationship.',
    href:  'https://www.akzeon.com/',
    label: 'Visit Akzeon →',
  },
  {
    logo:  '/img/partnerlogos/CCI-Professional-Full-Logo-08-600x102.webp',
    alt:   'CCI Professional',
    chap:  '▸ Partner · 03',
    role:  <em>Human capital &amp; change</em>,
    body:  'Human capital consulting firm working on Industry 4.0 enabled change. CCIP turns human potential into organisational performance, complementing the operational and systems side of our engagements. Strategy, change management, organisational leadership, and team mastery programmes.',
    href:  'https://www.cciprofessional.com/',
    label: 'Visit CCIP →',
  },
];

const Chapter = ({ band, roman, cat, title, lede, year, chap, head, prose, movesHead, moves, teamNote }) => (
  <section className={`section ${band} stitch--top`}>
    <div className="frame">
      <div className="sidehead">
        <aside className="sidehead__label">
          <div className="sidehead__roman">{roman}</div>
          <div className="sidehead__cat">{cat}</div>
        </aside>
        <div>
          <h2 className="sidehead__title">{title}</h2>
          <p className="sidehead__lede">{lede}</p>

          <div className="chronicle">
            <div className="chronicle__row">
              <div className="chronicle__year">{year}</div>
              <div className="chronicle__body">
                <div className="chronicle__chap">{chap}</div>
                <h3 className="chronicle__head">{head}</h3>
                <div className="chronicle__prose">
                  {prose.map((p, i) => <p key={i}>{p}</p>)}
                </div>

                <div className="chronicle__moves">
                  <div className="chronicle__moves-head">{movesHead}</div>
                  <ul className="chronicle__moves-list">
                    {moves.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>

                {teamNote && <p className="chronicle__team">{teamNote}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HistoryPage = () => {
  return (
    <div className="history-page">
      {/* ===== HERO ===== */}
      <section className="saga">
        <div className="saga__motif" aria-hidden="true"></div>
        <div className="frame">
          <div className="saga__grid">
            <div>
              <div className="eyebrow-rule">I · History</div>
              <div className="saga__wordmark">RussellStone Lighthouse</div>
              <h1 className="saga__title">From frustration, to <em>structure</em>, to the work we do now.</h1>
              <p className="saga__subcap">We learned to see systems the hard way, inside our own group, before we offered the approach externally.</p>
              <p className="saga__lede">
                The story below is short. Four chapters: years of running businesses without a reliable diagnosis, a 2023 discovery that gave us a structure, a 2024 build of ability, and the 2025 transition to formal external engagements.
              </p>
            </div>
            <div>
              <p className="saga__pull">
                We had a lot of experience and no way to structure it. We were running on <em>intuition</em> and <em>stamina</em>.
              </p>
              <div className="saga__attrib">— Pre-2023 · the frustration</div>
            </div>
          </div>
        </div>
        <div className="saga__horizon" aria-hidden="true"></div>
      </section>

      {/* ===== LEDGER ===== */}
      <section className="section section--short band--paper-warm stitch--top">
        <div className="frame">
          <div className="ledger">
            {ledger.map((l) => (
              <div key={l.big} className="ledger__cell">
                <div className="ledger__big">{l.big}</div>
                <div className="ledger__lbl">{l.lbl}</div>
                <div className="ledger__sub">{l.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CHAPTERS ===== */}
      {chapters.map((c) => <Chapter key={c.year} {...c} />)}

      {/* ===== PARTNER NETWORK ===== */}
      <section className="section band--paper-warm stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">VI</div>
              <div className="sidehead__cat">Network · the group &amp; partners</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Our group. Our partners.</h2>
              <p className="sidehead__lede">
                Where we sit, who we trained with, and who we work alongside when a specific problem needs specific expertise. No single firm does everything well.
              </p>

              <div className="dossier-card">
                <div className="dossier">
                  {partners.map((p) => (
                    <article key={p.alt} className="dossier__cell">
                      <div className="dossier__logo-wrap">
                        <img className="dossier__logo" src={p.logo} alt={p.alt} />
                      </div>
                      <div className="dossier__chap">{p.chap}</div>
                      <div className="dossier__role">{p.role}</div>
                      <p className="dossier__body">{p.body}</p>
                      <a className="dossier__link" href={p.href} target="_blank" rel="noopener noreferrer">
                        {p.label}
                      </a>
                    </article>
                  ))}
                </div>

                <p className="chronicle__team dossier__note">
                  The Group gives us stability and industry reach. Akzeon gives us the method and the mentorship. CCIP handles the human capital work that sits next to the operational work. The combination is how we cover the problems an engagement presents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="final-cta">
        <div className="final-cta__lines"></div>
        <div className="stage">
          <div className="eyebrow-rule">VII · The test</div>
          <h2 className="final-cta__title">The work is the test.</h2>
          <p className="final-cta__lede">
            Everything above is context. The question is whether the approach we built fits the problem you are holding. Tell us your situation and we will tell you honestly.
          </p>
          <div className="final-cta__row">
            <Link to="/principles" className="btn btn--ghost">Our principles</Link>
            <Link to="/contact"    className="btn btn--primary">
              Start a conversation <span className="arrow"></span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HistoryPage;
